import { Injectable } from '@angular/core';
import { EntityState, Order, QueryConfig, QueryEntity } from '@datorama/akita';
import { GuideRunStatus, NeedsType, PouchContentType } from '@libs/shared/interfaces/couch/models/core';
import {
  AnchorType,
  Assistant,
  Challenge,
  FabLoggable,
  GuideHeader,
  GuidePhase,
  GuidePhaseIncluded,
  GuidePhaseNeeds,
  GuidePouchContent,
  GuideTask,
  GuideTaskAlert,
  GuideTaskConfirm,
  GuideTaskLecture,
  GuideTaskQuestionnaire,
  GuideTaskTestmeal,
  PhaseAnchor,
} from '@libs/shared/interfaces/couch/models/guide';
import { GuideStore } from '@libs/shared/interfaces/couch/stores/guide';
import { map, Observable } from 'rxjs';

export type ChallengeWithMeals = Challenge & { meals: GuideTaskTestmeal[] };

@QueryConfig({
  sortBy: 'created',
  sortByOrder: Order.DESC,
})
@Injectable({
  providedIn: 'root',
})
export class GuideQuery extends QueryEntity<EntityState<GuidePouchContent, string>> {
  public all$ = this.selectAll();

  constructor(protected override store: GuideStore) {
    super(store);
  }

  public byGuideId(guideId: string): Observable<GuidePouchContent | undefined> {
    return this.selectEntity(`guide_${guideId}`);
  }

  public getByGuideId(guideId: string): GuidePouchContent | undefined {
    return this.getEntity(`guide_${guideId}`);
  }

  public header(guideId: string): Observable<GuideHeader | undefined> {
    return this.byGuideId(guideId).pipe(map((guide) => guide?.header));
  }

  public phases(guideId: string): Observable<GuidePhase[]> {
    return this.byGuideId(guideId).pipe(map((guide) => guide?.phases || []));
  }

  public phase(guideId: string, phaseId: string): Observable<GuidePhase | undefined> {
    return this.phases(guideId).pipe(map((phases) => phases.find((phase) => phase.id === phaseId)));
  }

  public phaseName(guideId: string, phaseId: string): Observable<string | undefined> {
    return this.phases(guideId).pipe(map((phases) => phases.find((phase) => phase.id === phaseId)?.name));
  }

  public getPhase(guideId: string, phaseId: string): GuidePhase | undefined {
    return this.getByGuideId(guideId)?.phases.find((phase) => phase.id === phaseId);
  }

  public assistants(guideId: string): Observable<Assistant[]> {
    return this.byGuideId(guideId).pipe(map((guide) => guide?.assistants || []));
  }

  public assistant(guideId: string, assistantId: string): Observable<Assistant | undefined> {
    return this.assistants(guideId).pipe(
      map((assistants) => assistants.find((assistant) => assistant.id === assistantId)),
    );
  }

  public getAssistants(guideId: string): Assistant[] {
    return this.getByGuideId(guideId)?.assistants || [];
  }

  public challenges(guideId: string): Observable<Challenge[]> {
    return this.byGuideId(guideId).pipe(map((guide) => guide?.challenges || []));
  }

  public challenge(guideId: string, challengeId: string): Observable<Challenge | undefined> {
    return this.challenges(guideId).pipe(
      map((challenges) => challenges.find((challenge) => challenge.id === challengeId)),
    );
  }

  public guideTasks(guideId: string): Observable<GuideTask[]> {
    return this.byGuideId(guideId).pipe(map((guide) => guide?.guideTasks || []));
  }

  public getGuideTasks(guideId: string): GuideTask[] {
    return this.getByGuideId(guideId)?.guideTasks || [];
  }

  public getGuideTask(guideId: string, taskId: string): GuideTask | undefined {
    return this.getGuideTasks(guideId).find((guideTask) => guideTask.id === taskId);
  }

  public guideTasksByPhase(guideId: string, phaseId: string): Observable<GuideTask[]> {
    return this.guideTasks(guideId).pipe(
      map((guideTasks) => this.filterGuideTasksByPhase(guideTasks, guideId, phaseId)),
    );
  }

  public getGuideTasksByPhase(guideId: string, phaseId: string): GuideTask[] {
    return this.filterGuideTasksByPhase(this.getGuideTasks(guideId), guideId, phaseId);
  }

  public guideTasksByType(guideId: string, type: PouchContentType): Observable<GuideTask[]> {
    return this.guideTasks(guideId).pipe(
      map((guideTasks) => guideTasks.filter((guideTask) => guideTask.type === type)),
    );
  }

  public getGuideTasksByType(guideId: string, type: PouchContentType): GuideTask[] {
    return this.getGuideTasks(guideId).filter((guideTask) => guideTask.type === type);
  }

  public guideTasksByPhaseAndType(guideId: string, phaseId: string, type: PouchContentType): Observable<GuideTask[]> {
    return this.guideTasksByPhase(guideId, phaseId).pipe(
      map((guideTasks) => guideTasks.filter((guideTask) => guideTask.type === type)),
    );
  }

  public getGuideTasksByPhaseAndType(guideId: string, phaseId: string, type: PouchContentType): GuideTask[] {
    return this.getGuideTasksByPhase(guideId, phaseId).filter((guideTask) => guideTask.type === type);
  }

  public instructionsByPhase(guideId: string, phaseId: string): Observable<GuideTaskLecture[]> {
    return (
      this.guideTasksByPhaseAndType(guideId, phaseId, PouchContentType.lecture) as Observable<GuideTaskLecture[]>
    ).pipe(map((guideTasks) => guideTasks.filter((guideTask) => guideTask.isManual)));
  }

  public needsSensorByPhase(guideId: string, phaseId: string): Observable<GuidePhaseNeeds | undefined> {
    return this.phase(guideId, phaseId).pipe(
      map((phase) => phase?.needs.find((need) => need.type === NeedsType.tg_sensor)),
    );
  }

  public includedSensorByPhase(guideId: string, phaseId: string): Observable<GuidePhaseIncluded | undefined> {
    return this.phase(guideId, phaseId).pipe(
      map((phase) => phase?.included.find((included) => included.type === NeedsType.tg_sensor)),
    );
  }

  public challengesByPhase(guideId: string, phaseId: string): Observable<Challenge[]> {
    return this.challenges(guideId).pipe(
      map((challenges) =>
        challenges.filter(
          (challenge) =>
            challenge.phases?.includes(phaseId) || this.getGuideTaskPhaseId(guideId, challenge.tasks[0]) === phaseId,
        ),
      ),
    );
  }

  public challengesByPhaseWithMeals(guideId: string, phaseId: string): Observable<ChallengeWithMeals[]> {
    return this.challengesByPhase(guideId, phaseId).pipe(
      map((challenges) => {
        return challenges.map((challenge) => {
          return {
            ...challenge,
            meals: this.getMealsByChallenge(guideId, phaseId, challenge.id),
          } as ChallengeWithMeals;
        });
      }),
    );
  }

  public mealsByPhase(guideId: string, phaseId: string): Observable<GuideTaskTestmeal[]> {
    return this.guideTasksByPhaseAndType(guideId, phaseId, PouchContentType.meal) as Observable<GuideTaskTestmeal[]>;
  }

  public getMealsByPhase(guideId: string, phaseId: string): GuideTaskTestmeal[] {
    return this.getGuideTasksByPhaseAndType(guideId, phaseId, PouchContentType.meal) as GuideTaskTestmeal[];
  }

  public mealsByChallenge(guideId: string, phaseId: string, challengeId: string): Observable<GuideTaskTestmeal[]> {
    return this.mealsByPhase(guideId, phaseId).pipe(
      map((meals) => meals.filter((meal) => this.getChallenge(guideId, challengeId)?.tasks.includes(meal.id))),
    );
  }

  public getMealsByChallenge(guideId: string, phaseId: string, challengeId: string): GuideTaskTestmeal[] {
    return this.getMealsByPhase(guideId, phaseId).filter((meal) =>
      this.getChallenge(guideId, challengeId)?.tasks.includes(meal.id),
    );
  }

  public mealsWithoutChallenge(guideId: string, phaseId: string): Observable<GuideTaskTestmeal[]> {
    return this.mealsByPhase(guideId, phaseId).pipe(
      map((meals) =>
        meals.filter((meal) => {
          let inChallenge = false;

          for (const challenge of this.getChallenges(guideId)) {
            if (challenge.tasks.includes(meal.id)) {
              inChallenge = true;
            }
          }

          return !inChallenge;
        }),
      ),
    );
  }

  public assistantsByPhase(guideId: string, phaseId: string): Observable<Assistant[]> {
    return this.assistants(guideId).pipe(
      map((assistants) => this.filterAssistantsByPhase(assistants, guideId, phaseId)),
    );
  }

  public getAssistantsByPhase(guideId: string, phaseId: string): Assistant[] {
    return this.filterAssistantsByPhase(this.getAssistants(guideId), guideId, phaseId);
  }

  public alerts(guideId: string): Observable<GuideTaskAlert[]> {
    return this.guideTasksByType(guideId, PouchContentType.alert) as Observable<GuideTaskAlert[]>;
  }

  public alertsByPhase(guideId: string, phaseId: string): Observable<GuideTaskAlert[]> {
    return this.guideTasksByPhaseAndType(guideId, phaseId, PouchContentType.alert) as Observable<GuideTaskAlert[]>;
  }

  public getAlertsByPhase(guideId: string, phaseId: string): GuideTaskAlert[] {
    return this.getGuideTasksByPhaseAndType(guideId, phaseId, PouchContentType.alert) as GuideTaskAlert[];
  }

  public lecturesByPhase(guideId: string, phaseId: string): Observable<GuideTaskLecture[]> {
    return (
      this.guideTasksByPhaseAndType(guideId, phaseId, PouchContentType.lecture) as Observable<GuideTaskLecture[]>
    ).pipe(map((guideTasks) => guideTasks.filter((guideTask) => !guideTask.isManual)));
  }

  public lectures(guideId: string): Observable<GuideTaskLecture[]> {
    return this.guideTasksByType(guideId, PouchContentType.lecture) as Observable<GuideTaskLecture[]>;
  }

  public lecture(guideId: string, lectureId: string): Observable<GuideTaskLecture | undefined> {
    return this.lectures(guideId).pipe(map((lectures) => lectures.find((lecture) => lecture.id === lectureId)));
  }

  public instructionByPhaseAndId(
    guideId: string,
    phaseId: string,
    instructionId: string,
  ): Observable<GuideTaskLecture | undefined> {
    return this.instructionsByPhase(guideId, phaseId).pipe(
      map((instructions) => instructions.find((instruction) => instruction.id === instructionId)),
    );
  }

  public confirmsByPhase(guideId: string, phaseId: string): Observable<GuideTaskConfirm[]> {
    return this.guideTasksByPhaseAndType(guideId, phaseId, PouchContentType.confirm) as Observable<GuideTaskConfirm[]>;
  }

  public confirms(guideId: string): Observable<GuideTaskConfirm[]> {
    return this.guideTasksByType(guideId, PouchContentType.confirm) as Observable<GuideTaskConfirm[]>;
  }

  public confirm(guideId: string, confirmId: string): Observable<GuideTaskConfirm | undefined> {
    return this.confirms(guideId).pipe(map((confirms) => confirms.find((confirm) => confirm.id === confirmId)));
  }

  public questionnairesByPhase(guideId: string, phaseId: string): Observable<GuideTaskQuestionnaire[]> {
    return this.guideTasksByPhaseAndType(guideId, phaseId, PouchContentType.questionnaire) as Observable<
      GuideTaskQuestionnaire[]
    >;
  }

  public questionnaires(guideId: string): Observable<GuideTaskQuestionnaire[]> {
    return this.guideTasksByType(guideId, PouchContentType.questionnaire) as Observable<GuideTaskQuestionnaire[]>;
  }

  public questionnaire(guideId: string, questionnaireId: string): Observable<GuideTaskQuestionnaire | undefined> {
    return this.questionnaires(guideId).pipe(
      map((questionnaires) => questionnaires.find((questionnaire) => questionnaire.id === questionnaireId)),
    );
  }

  public loggables(guideId: string): Observable<FabLoggable[]> {
    return this.byGuideId(guideId).pipe(map((guide) => guide?.loggables || []));
  }

  public loggablesByRunStatus(guideId: string, guideRunStatus: GuideRunStatus): Observable<FabLoggable[]> {
    return this.byGuideId(guideId).pipe(
      map(
        (guide) => guide?.loggables?.filter((loggable) => loggable.visible.runStatus[guideRunStatus] !== false) || [],
      ),
    );
  }

  public loggable(guideId: string, loggableId: string): Observable<FabLoggable | undefined> {
    return this.loggables(guideId).pipe(map((loggables) => loggables.find((loggable) => loggable.id === loggableId)));
  }

  public getGuideLoggable(guideId: string, loggableId: string): FabLoggable | undefined {
    return this.getByGuideId(guideId)?.loggables?.find((loggable) => loggable.id === loggableId);
  }

  public getMedicalGuides(): Observable<GuidePouchContent[]> {
    return this.all$.pipe(map((guides) => guides.filter((guide) => guide.header.medical?.isMedicalProduct)));
  }

  private isActivationPhase(guideId: string, phaseId: string): boolean {
    return this.getPhase(guideId, phaseId)?.timeProperties.anchor?.type === AnchorType.activation;
  }

  private isNeedsSensorPhase(guideId: string, phaseId: string): boolean {
    return !!this.getPhase(guideId, phaseId)?.needs.find((need) => need.type === NeedsType.tg_sensor);
  }

  private getNeedsSensorPhases(guideId: string): GuidePhase[] {
    return (
      this.getByGuideId(guideId)?.phases.filter((phase) =>
        phase.needs.find((need) => need.type === NeedsType.tg_sensor),
      ) || []
    );
  }

  private getNeedsSensorPhaseIndex(guideId: string, phaseId: string): number {
    return this.getNeedsSensorPhases(guideId).findIndex((phase) => phase.id === phaseId);
  }

  private getGuideTaskPhaseId(guideId: string, guideTaskId: string): string {
    return (
      (
        this.getByGuideId(guideId)?.guideTasks.find((guideTask) => guideTask.id === guideTaskId)?.timeProperties
          .anchor as PhaseAnchor
      )?.id || ''
    );
  }

  private filterAssistantsByPhase(assistants: Assistant[], guideId: string, phaseId: string): Assistant[] {
    return assistants.filter(
      (assistant) =>
        assistant.id !== '_challenges' &&
        ((assistant.timeProperties.anchor?.type === AnchorType.phase &&
          assistant.timeProperties.anchor.id === phaseId) ||
          (this.isActivationPhase(guideId, phaseId) &&
            assistant.timeProperties.anchor?.type === AnchorType.activation)),
    );
  }

  private filterGuideTasksByPhase(guideTasks: GuideTask[], guideId: string, phaseId: string): GuideTask[] {
    return guideTasks.filter(
      (guideTask) =>
        (guideTask.timeProperties.anchor?.type === AnchorType.phase &&
          guideTask.timeProperties.anchor.id === phaseId) ||
        (guideTask.timeProperties.anchor?.type === AnchorType.activation && this.isActivationPhase(guideId, phaseId)) ||
        (guideTask.timeProperties.anchor?.type === AnchorType.sensor &&
          this.isNeedsSensorPhase(guideId, phaseId) &&
          (guideTask.timeProperties.anchor.index === -1 ? 0 : guideTask.timeProperties.anchor.index || 0) ===
            this.getNeedsSensorPhaseIndex(guideId, phaseId)) ||
        (guideTask.timeProperties.anchor?.type === AnchorType.task &&
          this.getGuideTaskPhaseId(guideId, guideTask.timeProperties.anchor.id || '')) === phaseId,
    );
  }

  private getChallenges(guideId: string): Challenge[] {
    return this.getByGuideId(guideId)?.challenges || [];
  }

  private getChallenge(guideId: string, challengeId: string): Challenge | undefined {
    return this.getChallenges(guideId).find((challenge) => challenge.id === challengeId);
  }
}
